import { CCUtils } from "../../../../common/CCUtils"
import { COLOR } from "../../../../common/Color"
import { ButtonContainer, Buttons } from "../../../generic-sprites/Buttons"
import { PhaserText } from "../../../utils/PhaserText"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { KidsChoiceAudio, KidsChoiceImages } from "../KidsChoiceAssets"
import { KidsChoicePartOneState } from "../KidsChoicePartOneState"
import { KidsChoiceStore } from "../KidsChoiceStore"
import { KidsChoiceCategoryType, KidsChoiceHealthVideos, KidsChoiceSecondHandVideos, KidsChoiceSprite, KidsChoiceVideoChoice } from "../KidsChoiceTypes"
import { KidsChoiceVideoSelection } from "./KidsChoiceVideoSelection"

export class KidsChoiceVideoSelectContainer implements KidsChoiceSprite {

    private static categoryVideoChoices: Map<KidsChoiceCategoryType, Array<KidsChoiceVideoChoice>> = new Map([
        // Array literal notation here made tslint mad, but using the constructor seems to work fine...
        [KidsChoiceCategoryType.HEALTH_EFFECTS, [KidsChoiceHealthVideos.JUSTIN, KidsChoiceHealthVideos.REENA]],
        [KidsChoiceCategoryType.SECONDHAND_SMOKE, [KidsChoiceSecondHandVideos.NOWHERE, KidsChoiceSecondHandVideos.TRAPPED]]
    ])
    private static categoryIntroAudio: Map<KidsChoiceCategoryType, KidsChoiceAudio> = new Map([
        [KidsChoiceCategoryType.HEALTH_EFFECTS, KidsChoiceAudio.HEALTH_EFFECT_INTRO],
        [KidsChoiceCategoryType.SECONDHAND_SMOKE, KidsChoiceAudio.SECONDHAND_INTRO]
    ])
    private static categoryEndAudio: Map<KidsChoiceCategoryType, KidsChoiceAudio> = new Map([
        [KidsChoiceCategoryType.HEALTH_EFFECTS, KidsChoiceAudio.HEALTH_EFFECT_EXIT],
        [KidsChoiceCategoryType.SECONDHAND_SMOKE, KidsChoiceAudio.SECONDHAND_EXIT]
    ])

    submitVote: ButtonContainer
    backButton: ButtonContainer
    selections: Array<KidsChoiceVideoSelection>

    private readonly voteInfoGroup: Phaser.Group
    private title: Phaser.Text
    private voteAudio: boolean
    private yourFavorite: Phaser.Sprite

    constructor(
        private game: Phaser.Game,
        private type: KidsChoiceCategoryType,
        private group: Phaser.Group,
        onEnd: () => void
    ) {
        this.voteInfoGroup = this.game.add.group()

        this.voteAudio = false
        this.setCategoryTitle()
        this.setVoteButton(onEnd)
        const videos = this.type === KidsChoiceCategoryType.HEALTH_EFFECTS ? KidsChoiceHealthVideos : KidsChoiceSecondHandVideos
        this.selections = CCUtils.enumValues(videos).map((choice, index) =>
            new KidsChoiceVideoSelection(game, type, choice as KidsChoiceVideoChoice, index, this.onPlay, this.onComplete, this.onVote, group)
        )
        let introAudio
        if (KidsChoicePartOneState.IS_PART_ONE) {
            introAudio = this.game.add.audio(KidsChoiceVideoSelectContainer.categoryIntroAudio.get(type))
            introAudio.onStop.add(() => this.selections.forEach(selection => selection.enable()))
        } else {
            introAudio = this.game.add.audio(KidsChoiceAudio.RESULTS_FOR_CATEGORY)
            introAudio.onStop.add(() => {
                // Video votes
                const firstVideoKey: KidsChoiceVideoChoice = KidsChoiceVideoSelectContainer.categoryVideoChoices.get(type)[0]
                const firstVideoVotes: number = KidsChoiceStore.votesForVideo(this.type, firstVideoKey)
                const firstVotesText = `${firstVideoVotes.toString()} vote${firstVideoVotes === 1 ? "" : "s"}`
                PhaserText.create(this.game, firstVotesText, {
                    x: Scale.x(.31), y: Scale.y(.64), fontPx: 20, color: COLOR.brightYellow, group: this.voteInfoGroup
                })
                const secondVideoKey: KidsChoiceVideoChoice = KidsChoiceVideoSelectContainer.categoryVideoChoices.get(type)[1]
                const secondVideoVotes: number = KidsChoiceStore.votesForVideo(this.type, secondVideoKey)
                const secondVotesText = `${secondVideoVotes.toString()} vote${secondVideoVotes === 1 ? "" : "s"}`
                PhaserText.create(this.game, secondVotesText, {
                    x: Scale.x(.61), y: Scale.y(.64), fontPx: 20, color: COLOR.brightYellow, group: this.voteInfoGroup
                })

                // Total Votes
                const totalVotes: number = firstVideoVotes + secondVideoVotes
                const totalVotesText = `Total votes = ${totalVotes.toString()}`
                PhaserText.create(this.game, totalVotesText, {
                    x: Scale.x(.65), y: Scale.y(.73), fontPx: 18, color: COLOR.brightYellow, group: this.voteInfoGroup
                })

                // Favorite Marker
                const favoriteX = [KidsChoiceHealthVideos.JUSTIN, KidsChoiceSecondHandVideos.NOWHERE].includes(KidsChoiceStore.votedFor(this.type))
                    ? Scale.x(.3) : Scale.x(.61)
                this.yourFavorite = Sprites.add(this.game, KidsChoiceImages.YOUR_FAVORITE,
                    {x: favoriteX, y: Scale.y(.68), debug: true, group: this.voteInfoGroup})
                Scale.scaleSprite(this.yourFavorite, .08)

                // Back to categories
                this.backButton = Buttons.create(this.game, "Back to Categories", () => {
                    this.setViewedVotes()
                    this.voteInfoGroup.destroy()
                    onEnd()
                }, {
                    x: Scale.x(0.11), y: Scale.y(0.78), width: .13, textConfig: {fontPx: 12}, group: this.voteInfoGroup
                })

                this.backButton.disable()

                const watchAgainAudio = this.game.add.audio(KidsChoiceAudio.WATCH_VIDEOS_AGAIN)
                watchAgainAudio.onStop.addOnce(() => {
                    this.selections.forEach(selection => selection.enable())
                    this.backButton.enable()
                })
                watchAgainAudio.play()
            })
        }
        introAudio.onPlay.add(() => this.selections.forEach(selection => selection.disable()))

        introAudio.play()
    }

    onPlay = () => {
        this.title.alpha = 0
        this.selections.forEach(selection => {
            selection.hide()
            selection.voteButton.setVisible(false)
            this.submitVote.setInvisible()
            this.submitVote.disable()
        })

        // For part 2: Hide the voting info and disable the button
        this.voteInfoGroup.forEach(element => {
            element.alpha = 0
        })
        if (this.backButton) {
            this.backButton.disable()
        }
    }

    onComplete = () => {
        this.title.alpha = 1
        if (KidsChoicePartOneState.IS_PART_ONE) {
            KidsChoiceStore.instance.allWatched(this.type) ? this.afterAllWatched() : this.setWatchedVideos()
        } else {
            this.selections.forEach(selection => selection.enable())
        }

        // For part 2: Show the voting info and enable the button
        this.voteInfoGroup.forEach(element => {
            element.alpha = 1
        })
        if (this.backButton) {
            this.backButton.enable()
        }
    }

    onVote = () => {
        this.selections
            .filter(selection => selection.choice !== KidsChoiceStore.instance.votedForVideo(this.type))
            .forEach(selection => selection.voteButton.selected = false)
        this.checkVoted()
    }

    destroy = () => {
        this.title.destroy()
        this.submitVote.destroy()
        this.selections.forEach(selection => selection.destroy())
    }

    private afterAllWatched = () => {
        if (!this.voteAudio) {
            this.voteAudio = true
            this.submitVote.setVisible()
            const audio = this.game.add.audio(KidsChoiceVideoSelectContainer.categoryEndAudio.get(this.type))
            audio.onPlay.add(() => {
                this.selections.forEach(selection => {
                    selection.visible = true
                    selection.unsetWatched()
                    selection.disable()
                    selection.voteButton.setVisible(true)
                })
            })
            audio.onStop.add(() => {
                this.selections.forEach(selection => {
                    selection.enable()
                    selection.voteButton.enable()
                })
                this.checkVoted()
            })
            audio.play()
        } else {
            this.selections.forEach(selection => {
                selection.enable()
                selection.voteButton.setVisible(true)
                selection.voteButton.enable()
                this.submitVote.setVisible()
                this.checkVoted()
            })
        }
    }

    private checkVoted = () => KidsChoiceStore.instance.voteIsCast(this.type) ? this.submitVote.enable() : this.submitVote.disable()

    private setVoteButton = (onEnd: () => void) => {
        this.submitVote = Buttons.create(this.game, "Submit Vote", onEnd, {width: 0.2})
        Sprites.centerSpriteInSprite(KidsChoicePartOneState.centerRectangle, this.submitVote.button)
        this.submitVote.button.y += Scale.y(0.4)
        this.submitVote.center()
        this.submitVote.setInvisible()
        this.submitVote.disable()
    }

    private setCategoryTitle = () => {
        this.title = PhaserText.create(this.game, this.type, {
            group: this.group,
            color: COLOR.white,
        })
        Sprites.centerTextInSprite(KidsChoicePartOneState.centerRectangle, this.title)
        this.title.y += Scale.y(0.05)
    }

    private setWatchedVideos = () => this.selections.forEach(selection => {
        selection.enable()
        if (KidsChoiceStore.instance.watchedHealthVideos.includes(selection.choice)
            || KidsChoiceStore.instance.watchedSecondhandVideos.includes(selection.choice)) {
            selection.setWatched()
        }
    })

    private setViewedVotes = () => {
        if (this.type === KidsChoiceCategoryType.HEALTH_EFFECTS) {
            KidsChoiceStore.instance.viewedHealthVotes = true
        } else {
            KidsChoiceStore.instance.viewedSecondhandVotes = true
        }
    }
}

